import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/api";
import { changePlan } from "../../services/paymentService";
import PlansComponent from "../../components/Plans/PlansComponent";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import { openWhatsapp, changePlanMessage } from "./whatsappSupport";
import {
  resolveBillingMode,
  isSelfServiceBilling,
  resolveBillingModeNotice,
} from "../../utils/planCapabilities";
import styles from "./SubscriptionManagement.module.css";

const BILLING_ROUTE = "/subscription/billing";

function formatDatePtBr(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR").format(date);
}

/**
 * Rota autenticada /subscription/plans — sub-view da GRADE ("Ver todos os
 * planos"), acessada a partir da tela de gestão (/subscription/billing).
 *
 * Renderiza `PlansComponent` em MODO GESTÃO: o plano vigente (`currentPlanId`,
 * de GET /tenants/me/plan-usage → plan.id) é marcado "Seu plano atual"; um plano
 * com troca já agendada (`pendingPlanId`) é marcado "Troca agendada"; os demais
 * exibem "Mudar para este plano".
 *
 * Troca de plano (ADR-0019): clicar abre confirmação → POST /payments/change-plan.
 *  • { status: "scheduled", effectiveDate } → troca agendada (toast + marca pendente).
 *  • { status: "manual_fallback" } → cai no WhatsApp (fallback quando falta
 *    tokenização do cartão).
 *
 * É auto-suficiente (busca o próprio currentPlanId/pendingPlanId) para funcionar
 * em deep-link/refresh direto, não só via navegação a partir da gestão.
 */
export default function PlansGridPage() {
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [pendingPlanId, setPendingPlanId] = useState(null);
  const [nextBillingDate, setNextBillingDate] = useState(null);
  // Autoridade financeira (ADR-0034). Fail-safe `ASAAS` — o estado inicial já
  // é o do fail-safe, então a grade nunca aparece travada enquanto carrega e a
  // degradação silenciosa do fetch (abaixo) mantém o comportamento de hoje.
  const [billingMode, setBillingMode] = useState(() => resolveBillingMode(null));

  // Plano em confirmação (aberto no ConfirmDialog) e loading da troca.
  const [confirmPlan, setConfirmPlan] = useState(null);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.all([
      api.get("/tenants/me/plan-usage").catch(() => null),
      api.get("/tenants/me").catch(() => null),
    ]).then(([usageRes, meRes]) => {
      if (!active) return;
      // Falha ao descobrir o plano vigente não quebra a grade: apenas nenhum
      // card fica marcado.
      setCurrentPlanId(usageRes?.data?.plan?.id ?? null);
      setPendingPlanId(
        usageRes?.data?.pendingPlanId ?? meRes?.data?.tenant?.pendingPlanId ?? null,
      );
      setNextBillingDate(meRes?.data?.tenant?.nextBillingDate ?? null);
      setBillingMode(
        resolveBillingMode({
          billingMode:
            usageRes?.data?.billingMode ?? meRes?.data?.tenant?.billingMode,
        }),
      );
    });
    return () => {
      active = false;
    };
  }, []);

  const selfServiceBilling = isSelfServiceBilling(billingMode);
  const billingModeNotice = resolveBillingModeNotice(billingMode);

  // CTA de um plano não-vigente → abre a confirmação (não dispara nada ainda).
  //
  // ADR-0034 §7: troca de plano de tenant MANUAL/INTERNAL é operação
  // administrativa, não self-service. O caminho inteiro morre AQUI — a
  // confirmação nunca abre, logo `POST /payments/change-plan` nunca sai. O
  // aviso acima da grade diz por quê.
  const handleManageCta = (plan) => {
    if (!selfServiceBilling) return;
    setConfirmPlan(plan);
  };

  const closeConfirm = () => {
    if (changing) return;
    setConfirmPlan(null);
  };

  // Fallback manual (quando o backend responde manual_fallback): WhatsApp.
  const fallbackToWhatsapp = (planName) => {
    openWhatsapp(changePlanMessage(planName));
  };

  const handleConfirmChange = async () => {
    // Segunda tranca: o diálogo já não abre fora de ASAAS, mas a chamada
    // financeira é a coisa que precisa ser impossível — não só improvável.
    if (!confirmPlan || !selfServiceBilling) return;
    setChanging(true);
    try {
      const res = await changePlan(confirmPlan.id);
      const data = res?.data ?? {};

      if (data.status === "manual_fallback") {
        // Sem tokenização do cartão: a troca segue pelo atendimento humano.
        fallbackToWhatsapp(confirmPlan.name);
        setConfirmPlan(null);
        return;
      }

      // status === "scheduled" (ou qualquer sucesso não-fallback).
      const when = formatDatePtBr(data.effectiveDate ?? nextBillingDate);
      const planLabel = data.newPlan?.name ?? confirmPlan.name;
      toast.success(
        when !== "—"
          ? `Troca para o plano ${planLabel} agendada para ${when}.`
          : `Troca para o plano ${planLabel} agendada para o próximo ciclo.`,
      );
      // Marca o plano como pendente para refletir na grade sem novo fetch.
      setPendingPlanId(confirmPlan.id);
      setConfirmPlan(null);
    } catch (err) {
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Não foi possível trocar de plano agora. Tente novamente.",
      );
    } finally {
      setChanging(false);
    }
  };

  const confirmWhen = formatDatePtBr(nextBillingDate);

  return (
    <div className={styles.page}>
      <header className={styles.pageHead}>
        <div>
          <Link to={BILLING_ROUTE} className={styles.backLink}>
            ← Voltar para minha assinatura
          </Link>
          <h1 className={styles.pageTitle}>Todos os planos</h1>
          <p className={styles.pageSubtitle}>
            Escolha o plano desejado. A troca vale a partir da sua próxima
            cobrança.
          </p>
        </div>
      </header>

      <section className={styles.card} aria-labelledby="all-plans-title">
        <h2 id="all-plans-title" className={styles.sectionTitle}>
          Ver todos os planos
        </h2>
        {/* Aviso de modo (ADR-0034). Informativo: os planos continuam
            visíveis — o que não existe é a troca por conta própria. */}
        {billingModeNotice && (
          <div className={styles.modeNotice} role="status">
            <p className={styles.modeNoticeTitle}>{billingModeNotice.title}</p>
            <p className={styles.modeNoticeText}>{billingModeNotice.text}</p>
          </div>
        )}
        {/* `manageDisabled` é a trava VISUAL do mesmo gate do `handleManageCta`
            — o CTA de cada card fica desabilitado de verdade em vez de
            silenciosamente inerte. As duas travas coexistem: esta explica, a do
            handler impede (defesa em profundidade). */}
        <PlansComponent
          currentPlanId={currentPlanId}
          pendingPlanId={pendingPlanId}
          onManageCta={handleManageCta}
          manageDisabled={!selfServiceBilling}
        />
      </section>

      {/* ── Confirmação de troca de plano (ação REAL — ADR-0019) ─────────── */}
      <ConfirmDialog
        isOpen={selfServiceBilling && Boolean(confirmPlan)}
        title="Confirmar troca de plano"
        confirmLabel={changing ? "Trocando…" : "Confirmar troca"}
        cancelLabel="Voltar"
        confirmVariant="primary"
        confirmDisabled={changing}
        onConfirm={handleConfirmChange}
        onCancel={closeConfirm}
      >
        <p className={styles.dialogText}>
          {confirmWhen !== "—"
            ? `Seu plano muda para ${confirmPlan?.name ?? ""} no próximo ciclo (${confirmWhen}). O valor novo vale a partir da próxima cobrança.`
            : `Seu plano muda para ${confirmPlan?.name ?? ""} no próximo ciclo. O valor novo vale a partir da próxima cobrança.`}
        </p>
      </ConfirmDialog>
    </div>
  );
}
